import classNames from 'classnames'
import { useState } from 'react'

import { Button } from './ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog'
import { Label } from './ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select'
import { Textarea } from './ui/textarea'
import { ToastAction } from './ui/toast'
import { toast } from './ui/use-toast'

import {
  AWS_ACCESS_LEVELS,
  AwsAccessLevel,
  CreateAwsAccessRequestInput,
  JumpHostInstanceType,
  getAwsAccessBrokerClient
} from '@/api/aws-access-broker-client'

const accessLevelItems: { title: string; description: string; value: AwsAccessLevel }[] = [
  {
    title: 'Read-only',
    description: 'Inspect resources, read logs and metrics. No writes, no deploys.',
    value: AWS_ACCESS_LEVELS.READ_ONLY
  },
  {
    title: 'Developer',
    description: 'Day-to-day production support. Cannot deploy outside the CI/CD pipeline.',
    value: AWS_ACCESS_LEVELS.DEVELOPER
  },
  {
    title: 'Administrator',
    description: 'Full administrative access. Assume only if you absolutely need it.',
    value: AWS_ACCESS_LEVELS.ADMIN
  }
]

const instanceTypeItems: { title: string; description: string; value: JumpHostInstanceType }[] = [
  { title: 't3.micro', description: '2 vCPU · 1 GB — CLI tools, psql queries (default)', value: 't3.micro' },
  { title: 't3.small', description: '2 vCPU · 2 GB — light scripting', value: 't3.small' },
  { title: 't3.medium', description: '2 vCPU · 4 GB — data exports, heavier workloads', value: 't3.medium' },
  { title: 't3.large', description: '2 vCPU · 8 GB — bulk migrations', value: 't3.large' }
]

const durationItemsByLevel: Record<string, { title: string; value: number }[]> = {
  [AWS_ACCESS_LEVELS.READ_ONLY]: [
    { title: '30 minutes', value: 30 },
    { title: '60 minutes', value: 60 },
    { title: '2 hours', value: 120 },
    { title: '4 hours', value: 240 },
    { title: '8 hours', value: 480 },
    { title: '12 hours', value: 720 }
  ],
  [AWS_ACCESS_LEVELS.DEVELOPER]: [
    { title: '30 minutes', value: 30 },
    { title: '60 minutes', value: 60 },
    { title: '2 hours', value: 120 },
    { title: '4 hours', value: 240 },
    { title: '8 hours', value: 480 },
    { title: '12 hours', value: 720 }
  ],
  [AWS_ACCESS_LEVELS.ADMIN]: [
    { title: '30 minutes', value: 30 },
    { title: '60 minutes', value: 60 }
  ]
}

interface RequestAwsAccessDialogProps {
  requestedBy: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onRequested?: (requestId: string) => void
}

export function RequestAwsAccessDialog({
  requestedBy,
  open,
  onOpenChange,
  onRequested
}: RequestAwsAccessDialogProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [accessLevel, setAccessLevel] = useState<AwsAccessLevel | ''>(
    AWS_ACCESS_LEVELS.READ_ONLY
  )
  const [durationMinutes, setDurationMinutes] = useState<number>(60)
  const [instanceType, setInstanceType] = useState<JumpHostInstanceType>('t3.micro')

  const durationItems = durationItemsByLevel[accessLevel || AWS_ACCESS_LEVELS.READ_ONLY]
  const showInstanceType = Boolean(accessLevel)

  const handleAccessLevelChange = (level: AwsAccessLevel) => {
    setAccessLevel(level)
    const options = durationItemsByLevel[level]
    const maxAllowed = options[options.length - 1].value
    if (durationMinutes > maxAllowed) setDurationMinutes(maxAllowed)
  }
  const [reason, setReason] = useState('')

  const canSubmit = Boolean(accessLevel && reason.trim())

  const handleRequestAccess = async () => {
    if (!canSubmit) return

    setIsLoading(true)

    try {
      const input: CreateAwsAccessRequestInput = {
        access_level: accessLevel as AwsAccessLevel,
        duration_minutes: durationMinutes,
        reason: reason.trim(),
        instance_type: instanceType
      }

      const request = await getAwsAccessBrokerClient().createRequest(input, requestedBy)

      const { slack_permalink: slackPermalink } = request

      toast({
        title: 'Access requested',
        description: 'Your request was posted to #dev-security-access and is awaiting approval.',
        ...(slackPermalink && {
          action: (
            <ToastAction
              altText="Open the request in Slack"
              onClick={() => window.open(slackPermalink, '_blank', 'noopener,noreferrer')}
            >
              Open in Slack
            </ToastAction>
          )
        })
      })

      onRequested?.(request.id)
      onOpenChange(false)
      setReason('')
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error instanceof Error ? error.message : 'Could not submit request. Try again later.'
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-primary">Request production AWS access</DialogTitle>
          <DialogDescription>
            You'll confirm this request from Slack before any session is created. It's an
            out-of-band second factor, so approving your own request is fine.
          </DialogDescription>
        </DialogHeader>

        <div className="grid w-full gap-1.5">
          <Label>Access level</Label>
          <Select onValueChange={handleAccessLevelChange} value={accessLevel}>
            <SelectTrigger
              className={classNames({
                'text-primary': accessLevel,
                'text-placeholder': !accessLevel
              })}
            >
              <SelectValue placeholder="Select the access level" />
            </SelectTrigger>
            <SelectContent className="w-[462px]">
              {accessLevelItems.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  <span className="font-bold">{item.title}</span>
                  <br />
                  <span className="font-normal leading-snug text-muted-foreground">
                    {item.description}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid w-full gap-1.5">
          <Label>Session duration</Label>
          <Select
            onValueChange={(e: string) => setDurationMinutes(Number(e))}
            value={String(durationMinutes)}
          >
            <SelectTrigger className="text-primary">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {durationItems.map((item) => (
                <SelectItem key={item.value} value={String(item.value)}>
                  {item.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {showInstanceType && (
          <div className="grid w-full gap-1.5">
            <Label>Jump host instance type</Label>
            <Select
              onValueChange={(v: string) => setInstanceType(v as JumpHostInstanceType)}
              value={instanceType}
            >
              <SelectTrigger className="text-primary">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {instanceTypeItems.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    <span className="font-bold">{item.title}</span>
                    <br />
                    <span className="font-normal leading-snug text-muted-foreground">
                      {item.description}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid w-full gap-1.5">
          <Label htmlFor="access_reason">Reason for access</Label>
          <Textarea
            id="access_reason"
            onChange={(e) => setReason(e.target.value)}
            placeholder="Incident #482: roll back failed deployment of svc-notification-api in eu-central-1."
            value={reason}
          />
        </div>

        <DialogFooter className="items-center justify-between flex w-full">
          <Button
            disabled={!canSubmit}
            loading={isLoading}
            onClick={handleRequestAccess}
            type="submit"
            variant="destructive"
          >
            Request access
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
